/* eslint-disable @angular-eslint/directive-selector */
import { AfterViewInit, Directive, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { DateUnitsAvailabilityAndCounts } from '@brafton/atlas-api-client';
import * as Chart from 'chart.js';
import * as moment from 'moment';

@Directive({
  selector: '[bfMixedChart]',
})
export class BfMixedChartDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input() public profileAvailabilities!: Array<DateUnitsAvailabilityAndCounts>;

  public chart: any;
  private cvs: any;
  private ignoreOnChange = true;
  private element: ElementRef;

  public constructor(element: ElementRef) {
    this.element = element;
  }

  public ngAfterViewInit(): any {
    Chart.defaults.global.defaultFontFamily = "'Roboto', 'Helvetica Neue', 'sans-serif'";
    this.cvs = this.element.nativeElement;

    if (this.profileAvailabilities) this.refreshData();

    this.ignoreOnChange = false;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.ignoreOnChange === false && changes['profileAvailabilities'] && this.profileAvailabilities)
      this.refreshData();
  }

  public ngOnDestroy(): any {
    if (this.chart) {
      this.chart.destroy();
      this.chart = void 0;
    }
  }

  private refreshData() {
    this.ngOnDestroy();
    this.chart = this.buildChart(this.cvs);
  }

  private buildChart(cvs: any): any {
    const labels = this.profileAvailabilities.map((pa) => moment(pa.date).utc().format('Do'));

    const options: Chart.ChartOptions = {
      legend: {
        display: true,
        position: 'bottom',
        labels: { fontFamily: "'Roboto', 'Helvetica Neue', 'sans-serif'", fontSize: 13, padding: 10 },
      },
      responsive: true,
      maintainAspectRatio: false,
      scales: { yAxes: [{ ticks: { beginAtZero: true } }] },
      layout: { padding: { left: 12, right: 50, top: 6, bottom: -10 } },
      title: { display: false },
    };

    const datasets: any = this.buildDataSets();
    const opts = {
      type: 'bar',
      data: { labels: labels, datasets: datasets },
      options: options,
    };

    return new Chart(cvs, opts);
  }

  private buildDataSets(): Array<Chart.ChartDataSets> {
    const allocateds = this.profileAvailabilities.map((pf) => +(pf.assignedUnits || 0).toFixed(2)) || [];
    const availables = this.profileAvailabilities.map((pf) => +(pf.availableUnits || 0).toFixed(2)) || [];
    const completeds = this.profileAvailabilities.map((pf) => +(pf.completedUnits || 0).toFixed(2)) || [];
    const actionable = this.profileAvailabilities.map((pf) => +(pf.actionableUnits || 0).toFixed(2)) || [];
    const toBeScheduled = this.profileAvailabilities.map((pf) => +((pf.assignedUnits || 0) + (pf.toBeScheduledReadyUnits || 0) + (pf.toBeScheduledWaitingUnits || 0)).toFixed(2)) || [];

    const datasetAllocated: Chart.ChartDataSets = {
      type: 'line',
      data: allocateds,
      label: 'Assigned',
      borderColor: 'rgba(135,193,191,1.0)',
      backgroundColor: 'rgba(135,193,191,1.0)',
      pointStyle: 'circle',
      pointRadius: 5,
    };
    const datasetAvailable: Chart.ChartDataSets = {
      data: availables,
      label: 'Available',
      borderColor: 'rgba(241,215,58,1.0)',
      backgroundColor: 'rgba(241,215,58,0.2)',
      borderWidth: 2,
    };
    const datasetCompleted: Chart.ChartDataSets = {
      type: 'line',
      data: completeds,
      label: 'Delivered',
      borderColor: 'rgb(179, 190, 205, 1.0)',
      backgroundColor: 'rgb(179, 190, 205, 1.0)',
      // pointStyle: 'triangle',
      // pointRadius: 5,
    };
    const datasetActionable: Chart.ChartDataSets = {
      type: 'line',
      data: actionable,
      label: 'Actionable',
      borderColor: 'rgb(150, 193, 61, 1.0)',
      backgroundColor: 'rgb(150, 193, 61, 1.0)',
      // pointStyle: 'rectRounded',
      // pointRadius: 5,
    };
    const datasetToBeScheduled: Chart.ChartDataSets = {
      type: 'line',
      data: toBeScheduled,
      label: 'TBS',
      borderColor: 'rgba(51,140,164, 1.0)',
      backgroundColor: 'rgba(51,140,164, 1.0)',
      hidden: true,
    };

    const datasets: Array<Chart.ChartDataSets> = [
      datasetCompleted,
      datasetActionable,
      datasetAllocated,
      datasetAvailable,
      datasetToBeScheduled,
    ];

    if (!datasets) throw new Error(`charts configuration error, data or datasets field are required to render char}`);

    return datasets;
  }
}
